import getStore from '../store/store';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import { observer } from 'mobx-react-lite';
import { Label } from '@fluentui/react/lib/Label';
import loc from 'owa-localize';
import {
    correctionsSectionHeader,
    refinementsSectionHeader,
    calloutPremiumText,
} from './EditorOptions.locstring.json';
import * as React from 'react';
import CritiqueGroups from './CritiqueGroups';
import CritiqueTypeDefinition from '../store/schema/CritiqueTypeDefinition';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { isFakeFreeConsumerTestFlightOn } from '../utils/isFakeFreeConsumerTestFlightOn';

const styles = require('./EditorOptions.scss');

const CATEGORY_ID_FOR_SPELLING = '19a112ec-21fd-432a-9d1f-195e5e94a00b';
const CATEGORY_IDS_FOR_GRAMMAR = [
    '04aeb01b-175a-4385-9257-bfc5f7e4b650', // English
    '53fb1beb-1d6f-49d1-b31d-f8cdc57403ab', // Arabic
    'cc2afbc4-6083-4958-b528-f10b0816ca17', // Chinese Simplified
    'b7b7128b-923c-4490-9779-12b540e74107', // Chinese Traditional
    '63896928-4237-42f3-86b6-b12065930930', // Czech
    '2a39daa0-06d5-4388-b963-3dfc54758139', // Danish
    'c55392a5-91a2-4bdd-83ec-0f09216cc519', // Dutch
    'f5ca2fca-201a-4470-9bcf-97dafcc21c9a', // Finnish
    'c79dd882-87cf-463c-9b72-7ab4532599fb', // French
    'dc978939-1000-48a4-9093-770a17ab2bb9', // German
    '4bc4c175-ca99-4427-9c98-ccc94dd79625', // Hebrew
    '523c2e7f-b433-42c3-9c83-51203dc634d8', // Hungarian
    'f3d05a7b-2f02-49f5-9b2d-ee13d0018f25', // Italian
    '5d88fa0c-a455-4a5d-8e8c-ed3a20776993', // Japanese
    '6118f79d-f489-4859-84d1-cb021ead2c20', // Korean
    'a5e3944d-e3d5-4651-bbdb-7c2c734d5d08', // Norwegian Bokmaal
    '318627d3-cb3b-4821-8747-714cbbbe5f80', // Polish
    'c4a768f3-e6e1-4a16-b8aa-744490a34ea6', // Portuguese (European)
    '09e8a544-efa3-4e0e-951f-ba7470e61575', // Portuguese (Brazilian)
    '300c4e88-eccf-4796-992a-8130c74cf2f9', // Russian
    'a50b7055-1c5b-48b6-bc9a-5b6837559e25', // Spanish
    '8f3811b3-cc6f-4e34-a8e0-e5017129dc66', // Swedish
    'd053ef0b-7b97-48c8-be78-e7d933bee0fa', // Turkish
];

export interface CritiqueCategoriesProps {
    showRefinements: boolean;
    refinmentsEnabled: boolean;
}

export const CritiqueCategories = observer(function CritiqueCategories(
    props: CritiqueCategoriesProps
) {
    const store = getStore();
    const { showRefinements, refinmentsEnabled } = props;

    // Get a list of all of the critique type definitions and then filter by category.
    // We need to treat the spelling and grammar categories differently, but the other
    // categories can be handled the same way
    const critiqueTypeDefinitions: CritiqueTypeDefinition[] = [...store.defaultOptionsUI.values()];
    const spellingCritiques: CritiqueTypeDefinition[] = [];
    const grammarCritiques: CritiqueTypeDefinition[] = [];
    const refinementCritiques: CritiqueTypeDefinition[] = [];
    let grammarGroupId: string = '';

    critiqueTypeDefinitions.forEach(el => {
        if (CATEGORY_ID_FOR_SPELLING === el.categoryId) {
            spellingCritiques.push(el);
        } else if (CATEGORY_IDS_FOR_GRAMMAR.includes(el.categoryId)) {
            grammarCritiques.push(el);
            grammarGroupId = el.categoryId;
        } else {
            refinementCritiques.push(el);
        }
    });

    const categoryNames = store.critiqueTypeCategoryNames;
    const refinementsCategories: { id: string; name: string }[] = [];

    store.critiqueTypeCategoryNames.forEach((value: string, key: string) => {
        if (key !== CATEGORY_ID_FOR_SPELLING && !CATEGORY_IDS_FOR_GRAMMAR.includes(key)) {
            refinementsCategories.push({ id: key, name: value });
        }
    });
    const initialPros = { setInitialFocus: true };
    const tooltip =
        !refinmentsEnabled || isFakeFreeConsumerTestFlightOn()
            ? loc(calloutPremiumText)
            : undefined;
    return (
        <>
            <div role="region" aria-label={loc(correctionsSectionHeader)}>
                <Sticky stickyPosition={StickyPositionType.Header}>
                    <Label className={styles.stickyHeader}>{loc(correctionsSectionHeader)}</Label>
                </Sticky>
                <CritiqueGroups
                    critiqueCategoryNames={[
                        {
                            id: CATEGORY_ID_FOR_SPELLING,
                            name: categoryNames.get(CATEGORY_ID_FOR_SPELLING) || '',
                        },
                    ]}
                    items={spellingCritiques}
                    enabled={true}
                />
                {grammarGroupId && (
                    <CritiqueGroups
                        critiqueCategoryNames={[
                            {
                                id: grammarGroupId,
                                name: categoryNames.get(grammarGroupId) || '',
                            },
                        ]}
                        items={grammarCritiques}
                        enabled={true}
                    />
                )}
            </div>
            {((showRefinements && refinementsCategories.length > 0) ||
                isFakeFreeConsumerTestFlightOn()) && (
                <div role="region" aria-label={loc(refinementsSectionHeader)}>
                    <Sticky stickyPosition={StickyPositionType.Header}>
                        <Label className={styles.stickyHeader} disabled={!refinmentsEnabled}>
                            {loc(refinementsSectionHeader)}
                        </Label>
                    </Sticky>
                    <TooltipHost content={tooltip} closeDelay={500} calloutProps={initialPros}>
                        <CritiqueGroups
                            critiqueCategoryNames={refinementsCategories}
                            items={refinementCritiques}
                            enabled={refinmentsEnabled}
                        />
                    </TooltipHost>
                </div>
            )}
        </>
    );
});
